import type { HomeLocation, SetHomeLocationBody } from "@/lib/employee-api";

/**
 * The rules of the portal's home-location form (BUG-2026-015), kept apart from
 * the page so they can be tested directly.
 *
 * Save is offered only for a valid form that would change what is saved.
 * Every change notifies HR (BR-063), so a Save left enabled after saving — QA
 * 2026-09-26 — sent HR a fresh "Home location updated" on every press. The
 * server now ignores a save that changes nothing, and these rules judge a
 * change the way it does: the address trimmed or none, a blank radius meaning
 * the organisation's default, the coordinates to the seven decimal places the
 * column stores.
 */

/** The server clamps to this range (BR-062); the form refuses outside it. */
export const HOME_RADIUS_MIN_M = 50;
export const HOME_RADIUS_MAX_M = 500;

/** Home coordinates are stored as Decimal(10, 7). */
const COORDINATE_DECIMALS = 7;

/** The form as typed. */
export interface HomeLocationForm {
  latitude: string;
  longitude: string;
  address: string;
  /** Blank means the organisation's default — never that default copied in. */
  radiusM: string;
}

/** One of the two is null: the body to send, or what stops it. */
export interface ParsedHomeLocationForm {
  body: SetHomeLocationBody | null;
  /** In words for the employee. */
  problem: string | null;
}

function refused(problem: string): ParsedHomeLocationForm {
  return { body: null, problem };
}

export type SavedHomeLocation = HomeLocation & { latitude: number; longitude: number };

/** Has the employee saved a home location yet? */
export function hasHomeLocation(home: HomeLocation | null): home is SavedHomeLocation {
  return home?.latitude != null && home.longitude != null;
}

/**
 * The organisation's default radius, when the API reveals it. `effectiveRadiusM`
 * is that default only while the employee has no radius of their own; once
 * they have one, both fields carry theirs and the default is not known here —
 * so it is never presented as the default.
 */
export function organisationDefaultRadius(home: HomeLocation | null): number | null {
  return home && home.radiusM == null ? home.effectiveRadiusM : null;
}

/**
 * The form for what is saved. A radius left to the organisation's default
 * stays blank, so saving never pins the employee to today's default.
 */
export function formFromHome(home: HomeLocation | null): HomeLocationForm {
  return {
    latitude: home?.latitude?.toString() ?? "",
    longitude: home?.longitude?.toString() ?? "",
    address: home?.address ?? "",
    radiusM: home?.radiusM?.toString() ?? "",
  };
}

/** A typed number, or null for blank or anything that does not read as one. */
function parseNumber(typed: string): number | null {
  const s = typed.trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function sameCoordinate(typed: string, saved: number): boolean {
  const n = parseNumber(typed);
  return n != null && n.toFixed(COORDINATE_DECIMALS) === saved.toFixed(COORDINATE_DECIMALS);
}

function sameRadius(typed: string, saved: number | null): boolean {
  if (typed.trim() === "") return saved == null;
  return saved != null && parseNumber(typed) === saved;
}

/** Would saving this form store anything other than what is saved? */
export function homeLocationChanged(form: HomeLocationForm, home: HomeLocation | null): boolean {
  if (!hasHomeLocation(home)) {
    return Object.values(form).some((value) => value.trim() !== "");
  }
  return (
    !sameCoordinate(form.latitude, home.latitude) ||
    !sameCoordinate(form.longitude, home.longitude) ||
    (form.address.trim() || null) !== home.address ||
    !sameRadius(form.radiusM, home.radiusM)
  );
}

/**
 * The body to send, or what stops it — the API's own limits, so a form that
 * passes here is not refused there.
 */
export function parseHomeLocationForm(form: HomeLocationForm): ParsedHomeLocationForm {
  if (form.latitude.trim() === "" || form.longitude.trim() === "") {
    return refused("Enter a latitude and longitude, or use your current location.");
  }
  const latitude = parseNumber(form.latitude);
  if (latitude == null || Math.abs(latitude) > 90) {
    return refused("Latitude must be a number from -90 to 90.");
  }
  const longitude = parseNumber(form.longitude);
  if (longitude == null || Math.abs(longitude) > 180) {
    return refused("Longitude must be a number from -180 to 180.");
  }

  const body: SetHomeLocationBody = { latitude, longitude };
  const address = form.address.trim();
  if (address) body.address = address;

  if (form.radiusM.trim() !== "") {
    const radiusM = parseNumber(form.radiusM);
    if (
      radiusM == null ||
      !Number.isInteger(radiusM) ||
      radiusM < HOME_RADIUS_MIN_M ||
      radiusM > HOME_RADIUS_MAX_M
    ) {
      return refused(
        `Choose a radius from ${HOME_RADIUS_MIN_M} to ${HOME_RADIUS_MAX_M} m, or leave it blank for your organisation's default.`,
      );
    }
    body.radiusM = radiusM;
  }
  return { body, problem: null };
}
